import React, {
  useState,
  useEffect,
  useMemo,
  createContext,
} from 'react'
import auth0 from 'auth0-js'
import { browserHistory } from 'react-router'
import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'
import logo from '../assets/images/SupportPredictLogo.svg'
import {
  AUTH0_DOMAIN,
  AUTH0_CLIENT_ID,
  URL_BEFORE_LOGOUT,
  JWT_TOKEN,
  ACCESS_TOKEN,
  DEVICE_ID,
  DEVICE_OS,
  MANAGEMENT_API_TOKEN
} from '../constants/keys'
import { removeNodeById } from 'helpers/utils'
import { postMessageToBotWindow } from '../helpers/bot'

/**
 * @typedef {Object} AuthResult
 * @property {string} accessToken
 * @property {string} idToken
 * @property {Object} idTokenPayload
 * @property {Object} idTokenPayload['https://devicebits.com/app_metadata']
 * @property {Object} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.enabled
 * @property {Object} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer.admin
 * @property {string} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer.name
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer.edit
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer.reporting
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer.intents
 * @property {string[]} idTokenPayload['https://devicebits.com/app_metadata'].composer_v2.customer.assignedAssociatedCustomers
 * @property {Object} idTokenPayload['https://devicebits.com/app_metadata'].careassist
 * @property {string} idTokenPayload['https://devicebits.com/app_metadata'].careassist.name
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].careassist.enabled
 * @property {number} idTokenPayload['https://devicebits.com/app_metadata'].careassist.fivestar_enabled
 * @property {string[]} idTokenPayload['https://devicebits.com/app_metadata'].careassist.multiLoginCustomers
 * @property {string} idTokenPayload.iss
 * @property {string} idTokenPayload.sub
 * @property {string} idTokenPayload.aud
 * @property {number} idTokenPayload.iat
 * @property {number} idTokenPayload.exp
 * @property {null|Object} appState
 * @property {null|string} refreshToken
 * @property {string} state
 * @property {number} expiresIn
 * @property {string} tokenType
 * @property {string} scope
 */

/**
 * @typedef {Object} ProfileResponse
 * @property {string} email
 * @property {string} name
 * @property {string} nickname
 * @property {boolean} email_verified
 * @property {string} picture
 * @property {string} sub
 * @property {Object} sup
 * @property {string} updated_at
 * @property {string} user_id
 * @property {string} clientID
 * @property {string} created_at
 * @property {Object} user_metadata
 * @property {Object[]} user_metadata.recent_topics
 * @property {string} user_metadata.recent_topics.contentType
 * @property {number} user_metadata.recent_topics.contentId
 * @property {number} user_metadata.recent_topics.deviceId
 * @property {string} user_metadata.recent_topics.contentTitle
 * @property {string} user_metadata.recent_topics.customer
 * @property {Object} app_metadata
 * @property {Object} app_metadata.composer_v2
 * @property {number} app_metadata.composer_v2.enabled
 * @property {Object} app_metadata.composer_v2.customer
 * @property {number} app_metadata.composer_v2.customer.admin
 * @property {string} app_metadata.composer_v2.customer.name
 * @property {number} app_metadata.composer_v2.customer.edit
 * @property {number} app_metadata.composer_v2.customer.reporting
 * @property {number} app_metadata.composer_v2.customer.intents
 * @property {string[]} app_metadata.composer_v2.customer.assignedAssociatedCustomers
 * @property {Object} app_metadata.careassist
 * @property {string} app_metadata.careassist.name
 * @property {number} app_metadata.careassist.enabled
 * @property {number} app_metadata.careassist.fivestar_enabled
 * @property {string[]} app_metadata.careassist.multiLoginCustomers
 * @property {Object} sup
 * @property {Object[]} identities
 * @property {string} identities.userId
 * @property {string} identities.provider
 * @property {string} identities.connection
 * @property {boolean} identities.isSocial
 * @property {Object} ['https://devicebits.com/app_metadata']
 * @property {Object} ['https://devicebits.com/app_metadata'].composer_v2
 * @property {number} ['https://devicebits.com/app_metadata'].composer_v2.enabled
 * @property {Object} ['https://devicebits.com/app_metadata'].composer_v2.customer
 * @property {number} ['https://devicebits.com/app_metadata'].composer_v2.customer.admin
 * @property {string} ['https://devicebits.com/app_metadata'].composer_v2.customer.name
 * @property {number} ['https://devicebits.com/app_metadata'].composer_v2.customer.edit
 * @property {number} ['https://devicebits.com/app_metadata'].composer_v2.customer.reporting
 * @property {number} ['https://devicebits.com/app_metadata'].composer_v2.customer.intents
 * @property {string[]} ['https://devicebits.com/app_metadata'].composer_v2.customer.assignedAssociatedCustomers
 * @property {Object} ['https://devicebits.com/app_metadata'].careassist
 * @property {string} ['https://devicebits.com/app_metadata'].careassist.name
 * @property {number} ['https://devicebits.com/app_metadata'].careassist.enabled
 * @property {number} ['https://devicebits.com/app_metadata'].careassist.fivestar_enabled
 * @property {string[]} ['https://devicebits.com/app_metadata'].careassist.multiLoginCustomers
 * @property {Object} sup
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {function(): void} logout
 * @property {function(): boolean} loggedIn
 * @property {function(): Promise<ProfileResponse>} getProfile
 * @property {function(any, Function): void} requireAuth
 * @property {any} lock
 * @property {function(): void} login
 * @property {WebAuth} webAuth
 */


const initialState = {}

/**
 * @type {React.Context<AuthContextValue | undefined>}
 */
export const AuthContext = createContext(initialState)

/**
 * The AuthProvider function is a React component that provides the AuthContext to its children.
 * It uses the useMemo hook to memoize an object containing all of the auth functions and values.
 * This allows us to avoid re-rendering components when we don't need to, which improves performance.
 *
 * @function
 * @name AuthProvider
 *
 * @see {@link https://community.auth0.com/t/example-script-works-everywhere-except-in-ie/20801 Auth0 Community}
 * @see {@link https://auth0.com/docs/libraries/lock/lock-configuration Auth0 Lock Configuration}
 * @see {@link https://auth0.com/docs/libraries/lock/lock-api-reference Auth0 Lock API Reference}
 * @param {Object} props - The props for the AuthProvider component.
 * @param {string} props.clientId - The client ID for Auth0.
 * @param {string} props.domain - The domain for Auth0.
 * @param {string} props.redirectUrl - The URL to redirect to after login.
 * @param {React.ReactNode} props.children - The child components to render.
 * @returns {React.JSX} A context object that can be used to get the auth value
 */
function AuthProvider({
  clientId = AUTH0_CLIENT_ID,
  domain = AUTH0_DOMAIN,
  redirectUrl = `${document.location.origin}/login`,
  children
}) {
  /**
   * @link https://auth0.com/docs/libraries/lock/lock-configuration
   * @link {Auth0LockStatic https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/auth0-lock/index.d.ts#L239}
   * @type {'Auth0LockStatic'} Quote as Auth0 is not imported see link above
   */
  const [lock] = useState(() => new window.Auth0Lock(clientId, domain, {
      auth: {
        // The URL Auth0 will redirect back to after authentication. Defaults to the empty string "" (no redirect URL)
        redirectUrl,
        // Should be set to "token" for SPA and "code" otherwise. Defaults to "code" when redirectUrl is provided, and to "token" otherwise.
        responseType: 'token',
        // You can send parameters when starting a login by adding them to the options object.
        // The following parameters are supported: scope, device, nonce and state.
        // @link https://auth0.com/docs/libraries/lock/lock-authentication-parameters
        params: {
          // There are different values supported for scope.
          // Keep in mind that JWTs are sent on every API request, so it is desirable to keep them as small as possible.
          // @link https://auth0.com/docs/libraries/lock/lock-authentication-parameters#scope-string-
          scope: 'openid'
        }
      },
      // This is the dictionary that we use to store the language identifiers. It's a bit tricky because we don't want to keep track of the dictionary
      languageDictionary: {
        title: '',
        // This action is called when the user clicks the forgot password button. It does not return
        forgotPasswordAction: 'Forgot or Change Password'
      },
      // This is the top - level function that defines the theme.
      theme: {
        logo,
        primaryColor: 'var(--main-color)'
      },
      // This is called by login.js to remember the last login time for the user
      rememberLastLogin: false,
      // Enables or disables closable controls on the form. This is a no - op if the form is not open
      closable: false,
      // When set to false, hides the login and sign up tabs in the login screen, making the sign up screen unreachable.
      allowSignUp: false,
      // This is an example of how to use this plugin. You can create your own plugin by calling this
      allowedConnections: ['Username-Password-Authentication']
    })
  )

  /**
   * Creates a WebAuth object that is used to authenticate requests to OpenID servers.
   * @link https://auth0.com/docs/libraries/auth0js/v9#webauth-object
   * @type {auth0.WebAuth}
   */
  const [webAuth] = useState(() => new auth0.WebAuth({
      domain,
      clientID: clientId,
      redirectUri: document.location.origin,
      scope: 'profile email read:current_user update:current_user_metadata',
      responseType: 'token id_token'
    })
  )

  let refreshTimeout;

  /**
   * Refreshes the management API access token by checking the current session
   * and updating the token in localStorage. Ensures the management API token
   * remains valid and can be used for API calls.
   * @returns {Promise<string>} Promise that resolves to the refreshed management api access token.
   * @throws {Error} If the WebAuth or Lock instances are not defined, or
   *      if there is an error checking the session.
   */
  const refreshManagementToken = () => {
    if (!webAuth || !lock) return Promise.reject(new Error('WebAuth or lock is not defined'))
    return new Promise((resolve, reject) => {
      webAuth.checkSession({
        audience: `https://${AUTH0_DOMAIN}/api/v2/`,
        scope: 'read:current_user update:current_user_metadata'
      }, (err, authResult) => {
        if (err) {
          console.error('Session Check Error:', err);
          reject(err);
        } else {
          // Sets a new management API token on every refresh after login
          localStorage.setItem(MANAGEMENT_API_TOKEN, authResult.accessToken);
          console.info('Token fetched successfully!');
          resolve(authResult.accessToken);
        }
      });
    }).catch(error => {
      console.error('Caught error in promise to get management api token', error)
      lock.show({
        flashMessage: {
          type: 'error',
          text: 'Error getting management API token.'
        }
      })
      throw error;
    });
  };

  /**
   * Handles token refresh scheduling based on the token's expiration time.
   * Ideally, the new token is created every 2 hours.
   * If the token is expired, it immediately refreshes the token. Otherwise, it schedules a refresh 5 minutes before the token expires.
   * @param {string} token - Management api token
   * @returns {void}
   */
  const handleTokenRefresh = token => {
    if (!token) throw new Error('No token in authResult')
    const decodedToken = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);

    const timeLeft = decodedToken.exp - now;

    if (timeLeft <= 0) {
      console.log('Token refreshed')
      return refreshManagementToken();
    }
    const refreshTime = (timeLeft - 300) * 1000;
    refreshTimeout = setTimeout(refreshManagementToken, refreshTime)
  };

  /**
   * Stores the access_token and id_token in local storage when the user is authenticated successfully
   * Navigates to the last url the user was on before authentication else navigates to the home page
   *
   * @param {AuthResult} authResult - The authentication results
   * @returns {Promise<void>}
   */
  const doAuthentication = async authResult => {
    if (!authResult.idToken || !authResult.accessToken)
      throw new Error('No token in authResult')
    localStorage.setItem(ACCESS_TOKEN, authResult.accessToken)
    localStorage.setItem(JWT_TOKEN, authResult.idToken)
    await refreshManagementToken();
    const urlBeforeLogout = localStorage.getItem(URL_BEFORE_LOGOUT)
    browserHistory.replace(urlBeforeLogout || '/')
  }

  /**
   * Displays the lock widget in the DOM.
   * This is called by login.js to display the lock widget and is expected to be at '/login' path
   * @returns {void}
   */
  const login = () => {
    lock.show()
  }

  /**
   * Checks if the user is logged in by returning true if there is an access token in local storage.
   * @returns {boolean}
   */
  const loggedIn = () => {
    return !!localStorage.getItem(ACCESS_TOKEN)
  }

  /**
   * Returns true if the token is expired
   * Used to prevent accidental refresh and is specific to agent users requiring uninterrupted access to the app
   * @todo revise the value of 1000ms
   * @returns {boolean}
   */
  const isExpired = () => {
    const token = localStorage.getItem(JWT_TOKEN)
    const payload = jwtDecode(token)
    // Returns true if exp is older than 1000ms (1 second)
    return payload.exp < Date.now() / 1000
  }

  /**
   * Renews a token and returns a promise that resolves to the new access token
   * Used to get a new access token after a period of time
   *
   * @returns {Promise<AuthResult>} A promise that resolves to an object containing the new access token and id token
   */
  const renewToken = () => {
    if (!webAuth || !lock) return Promise.reject(new Error('WebAuth or lock is not defined'))
    return new Promise((resolve, reject) => {
      webAuth.renewAuth(
        {
          audience: '',
          scope: 'openid',
          redirectUri: `${document.location.origin}/silent.html`,
          // This is a hack to fix IE bug #4243. We don't want to postMessageOrigin to document.location.origin because it's the same as postMessage
          postMessageOrigin: document.location.origin,
          // Enables postMessage to be used in messages. This is the default behavior. Use this if you want to use an HTTP POST or other kind of message
          usePostMessage: true
        },
        (err, authResult) => {
          if (err) {
            console.error('Error renewing auth', err)
            reject(err)
          } else {
            console.info('Successfully renewed auth')
            resolve(authResult)
          }
        }
      )
    })
      .catch(error => {
        console.error('Error renewing Auth', error)
        lock.show({
          flashMessage: {
            type: 'error',
            text: error.errorDescription
          }
        })
        throw error
      })

  }

  /**
   * Retrieves the access token from local storage or from the API.
   * If the token is expired, it will be renewed and returned.
   * @returns {Promise<string>} The access token either from local storage or from the API
   */
  const getToken = async () => {
    if (isExpired()) {
      // Renew token and return token. This is called by auth.js when it needs to be renewed
      const authResult = await renewToken()
      localStorage.setItem(ACCESS_TOKEN, authResult.accessToken)
      localStorage.setItem(JWT_TOKEN, authResult.idToken)
      return authResult.accessToken
    }
    return localStorage.getItem(ACCESS_TOKEN)
  }

  /**
   * Retrieves the user profile data from the API.
   *
   * The profile data includes information such as the user's name, email,
   * avatar URL, and other public profile information.
   *
   * @returns {Promise<ProfileResponse>} A Promise that resolves with the profile data after the user has logged in.
   */
  const getProfile = () => {
    if (!lock) return Promise.reject(new Error('Lock is not defined'))
    return new Promise((resolve, reject) => {
      getToken()
        .then(token => {
          lock.getUserInfo(token, (error, profile) => {
            if (error) {
              console.error('Error retrieving user profile', error)
              reject(error)
            } else {
              console.info(`Successfully retrieved user profile ${profile.nickname || profile.name || profile.email || ''}`)
              resolve(profile)
            }
          })
        })
        .catch(error => {
          console.error('Caught error in promise to retrieve user profile', error)
          lock.show({
            flashMessage: {
              type: 'error',
              text: error.error_description
            }
          })
          reject(error)
        })
    })
  }

  /**
   * Logs the user out of the application.
   * This will also remove the user's session and cookie data from localStorage and browserHistory.
   * If a QnABot (lex-web-ui) is mounted, its root Node will be removed from the DOM.
   * @see https://github.com/devicebits/qnabot-web-widget
   * @returns {void}
   */
  const logout = () => {
    localStorage.clear()
    sessionStorage.clear()

    // Removes the lex-web-ui root node from the DOM.
    removeNodeById('lex-web-ui-iframe')
    removeNodeById('bot-iframe-container')
    removeNodeById('bot-launcher-button-container')
    Cookies.remove(DEVICE_ID)
    Cookies.remove(DEVICE_OS)
    if (window.botWindowReference) {
      // Send "confirmLogout" message to the bot window
      postMessageToBotWindow( { event: 'confirmLogout' }, '*')
      removeNodeById('lex-web-ui')
    }
    browserHistory.replace('/login')
  }

  /**
   * A middleware on react-router schema that checks if the user is logged in.
   * If they are not, they will be redirected to the login page.
   * @param {*} nextState
   * @param {function} replace
   */
  const requireAuth = (nextState, replace) => {
    if (!loggedIn()) {
      localStorage.setItem(URL_BEFORE_LOGOUT, window.location.pathname)
      replace({ pathname: '/login' })
    }
  }

  /**
   * Handles authentication errors by logging the error and displaying a flash message.
   * If the lock instance is not defined, it returns a rejected promise.
   *
   * @param {Object} error - The error object containing details about the authentication error.
   * @param {string} context - A string indicating the context in which the error occurred.
   * @returns {Promise<never>} A rejected promise if the lock instance is not defined.
   */
  const handleAuthError = (error, context) => {
    if (!lock) return Promise.reject(new Error('Lock is not defined'))
    console.error(`Auth Error [${context}]:`, error);
    lock.show({
      flashMessage: {
        type: 'error',
        text: error.errorDescription || 'Authentication error occurred'
      }
    });
   login()
  };

  /**
   * Bind the named lock rules and events to the lock object instance.
   */
  useEffect(() => {
    // on auth success, call function to save authResults
    lock.on('authenticated', doAuthentication)
    // On auth error, show error flash message then call login()
    lock.on('authorization_error', error => handleAuthError(error, 'authorization_error'));
    // on unrecoverable error, show error flash message then call login()
    lock.on('unrecoverable_error', error => handleAuthError(error, 'unrecoverable_error'));
  }, [lock])

  /**
   * Effect to initialize management api access token refresh logic on component mount.
   * Checks if a valid token exists and schedules token refresh if necessary every 2 hours.
   */
  useEffect(() => {
    const managementApiToken = localStorage.getItem(MANAGEMENT_API_TOKEN);

    if (managementApiToken) {
      handleTokenRefresh(managementApiToken);
    }

    // Cleanup function to clear any timers when the component unmounts
    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, []);

/**
 * Adds a "To file a ticket in RISE, click here" link inside the Auth0 Lock popup **only on the main login screen**.
 * 
 * The link is injected after the popup content is rendered, styled with hover underline,
 * and automatically removed when the user navigates to Forgot Password, Change Password,
 * or Sign Up screens.
 *
 * @function useEffect
 * @description React effect that initializes Auth0 Lock and injects a custom
 * support link inside the login popup.
 * @param {Auth0Lock} lock - The Auth0 Lock instance used for authentication.
 */

useEffect(() => {
  if (!document.getElementById('auth0-custom-style')) {
    const style = document.createElement('style');
    style.id = 'auth0-custom-style';
    style.textContent = `
      .custom-support-link a {
        color: #337AB7;
        text-decoration: none;
      }
      .custom-support-link a:hover {
        text-decoration: underline;
      }
    `;
    document.head.appendChild(style);
  }

  const updateSupportLink = () => {
    const form = document.querySelector('.auth0-lock-form');
    if (!form) return;

    const hasEmail = !!form.querySelector('input[name="username"], input[name="email"]');
    const hasPassword = !!form.querySelector('input[type="password"]');
    const isMainLogin = hasEmail && hasPassword;

    const existingLink = form.querySelector('.custom-support-link');

    if (isMainLogin && !existingLink) {
      const linkDiv = document.createElement('div');
      linkDiv.className = 'custom-support-link';
      linkDiv.style.cssText = `
        text-align:center;
        margin-top:10px;
        font-size:13px;
        color:rgba(0,0,0,0.87);
      `;
 linkDiv.innerHTML = 'To file a ticket in RISE, click <a href="https://trc-amc.ivanticloud.com/Modules/SelfService/#serviceCatalog/request/1B0EF8116D4249729ACBFE23C130AE1C" target="_blank"> here</a>';      form.appendChild(linkDiv);
    } else if (!isMainLogin && existingLink) {
      existingLink.remove();
    }
  };

  const observer = new MutationObserver(updateSupportLink);
  observer.observe(document.body, { childList: true, subtree: true });

  // Cleanup
  lock.on('hide', () => observer.disconnect());
  return () => observer.disconnect();
}, [lock]);

  /**
   * Adds a memoized version of the auth value that is used to keep track of the state
   * @type {AuthContextValue}
   */
  const authValue = useMemo(
    () => ({
      lock,
      webAuth,
      login,
      loggedIn,
      logout,
      getProfile,
      requireAuth,
    }),
    [
      loggedIn
    ]
  )

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  )
}

export default AuthProvider